// Send unlock-minutes to a friend (web). Debits the caller, credits the
// recipient — friends-only, instant, irreversible. Mirrors the iOS
// SendMinutesSheet against POST /rewards/transfer. Modal chrome follows
// edit-class-modal.tsx.
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getFriends } from "@/api/friends";
import { getWallet, transferMinutes } from "@/api/rewards";
import { Alert, Button } from "@/components/ui";

export function SendMinutesModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const friends = useQuery({ queryKey: ["friends"], queryFn: getFriends });
  const wallet = useQuery({ queryKey: ["rewards-wallet"], queryFn: getWallet });

  const balance = wallet.data?.balance_minutes ?? 0;
  const [recipient, setRecipient] = useState("");
  const [minutes, setMinutes] = useState(15);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const send = useMutation({
    mutationFn: () => transferMinutes(recipient, minutes, crypto.randomUUID()),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["rewards-wallet"] });
      if (res.applied === false) {
        setErr(t("Not enough minutes — you have {{count}}.", { count: res.balance_minutes }));
        return;
      }
      const who = friends.data?.find((f) => f.user_id === recipient)?.display_name ?? t("your friend");
      setDone(t("Sent {{count}} minutes to {{who}}.", { count: minutes, who }));
    },
    onError: (e) => setErr(e instanceof Error ? e.message : t("Couldn't send minutes.")),
  });

  const canSend =
    recipient !== "" && minutes >= 1 && minutes <= balance && !send.isPending && !done;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="flex w-full max-w-md flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-black/10 p-4">
          <h2 className="text-lg font-bold">{t("Send minutes to a friend")}</h2>
          <button type="button" onClick={onClose} className="text-ink-faint hover:text-ink">
            ✕
          </button>
        </div>

        <div className="space-y-4 p-4">
          {err && <Alert>{err}</Alert>}
          {done ? (
            <>
              <p className="text-sm text-ink">{done}</p>
              <Button onClick={onClose} className="w-full">
                {t("Done")}
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm text-ink-soft">
                {t("You have {{count}} unlock minutes available.", { count: balance })}
              </p>

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink-soft">{t("Friend")}</span>
                <select
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  className="w-full rounded-xl border border-black/10 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                >
                  <option value="">{t("Choose a friend…")}</option>
                  {(friends.data ?? []).map((f) => (
                    <option key={f.user_id} value={f.user_id}>
                      {f.display_name}
                      {f.username ? ` (@${f.username})` : ""}
                    </option>
                  ))}
                </select>
                {friends.data && friends.data.length === 0 && (
                  <span className="mt-1 block text-xs text-ink-faint">
                    {t("Add a friend first to send them minutes.")}
                  </span>
                )}
              </label>

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink-soft">{t("Minutes")}</span>
                <input
                  type="number"
                  min={1}
                  max={balance}
                  value={minutes}
                  onChange={(e) => setMinutes(Math.max(1, Math.min(balance, Number(e.target.value) || 0)))}
                  className="w-full rounded-xl border border-black/10 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                />
              </label>

              <p className="text-xs text-ink-faint">
                {t("Minutes leave your balance and land on your friend's. This can't be undone.")}
              </p>

              <Button onClick={() => send.mutate()} disabled={!canSend} className="w-full">
                {send.isPending ? t("Sending…") : t("Send minutes")}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** Small button that opens the send-minutes modal. Drop it near the wallet. */
export function SendMinutesButton() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-full bg-brand-50 px-3 py-1.5 text-xs font-semibold text-brand-700 hover:bg-brand-100"
      >
        {t("Send minutes to a friend")}
      </button>
      {open && <SendMinutesModal onClose={() => setOpen(false)} />}
    </>
  );
}
